// TUS uploader wrapper for file-share orders.
//
// Wraps tus-js-client so the rest of the app can stay in async/await land.
// Resumability comes from tus-js-client's own urlStorage (default: localStorage)
// — keyed by file fingerprint (name+size+type+lastModified), so the same file
// picked again on the next session continues from the last committed offset.
//
// Order-level recovery (which files belong to which order, plus the share
// settings the user already entered) is persisted separately by the caller;
// see SESSION_KEY in FileShareController.

import * as tus from "tus-js-client"
import { ep, getCurrentUser } from "aldoo-ra/API"
import { getValue } from "aldoo-ra/Value"

const TUS_ENDPOINT = ep("order/tus")

// Per-chunk size sent over the wire. R2 multipart parts must be ≥5MB except
// the last; 8MB gives a comfortable margin and keeps PATCH count reasonable.
const CHUNK_SIZE = 8 * 1024 * 1024

// Retry schedule for transient failures (network blips, 5xx). tus-js-client
// retries automatically and re-sends from the committed offset.
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000, 30000]

// Mirrors the resolution chain used by aldoo-ra/API post(): module-level
// USER_CREDENTIALS first, then the Value store directly (covers the boot race
// where AldooAPI's useEffect hasn't run yet when an upload kicks off). Without
// the fallback, every fresh-tab upload start would hit 401 from the tus server.
function getAuthToken() {
  let creds = getCurrentUser()
  if (!creds && typeof window !== "undefined") {
    try { creds = getValue("user-credentials", "user-credentials") } catch {}
  }
  return creds?.auth_token || ""
}

// Wait briefly for credentials to materialise. Called once before kicking off
// a batch so the first tus POST doesn't race the React effect that populates
// USER_CREDENTIALS.
async function awaitAuth(timeoutMs = 2000) {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    if (getAuthToken()) return true
    await new Promise(r => setTimeout(r, 50))
  }
  return !!getAuthToken()
}

// Upload a single file via tus. Returns a Promise that resolves when the file
// finishes (HEAD says complete) and rejects on hard error / abort.
//
// `signal`: AbortSignal. When aborted, the upload is paused (.abort(false))
// so the URL stays valid for resume; pass `discardURL=true` only if you want
// to permanently abandon the upload (e.g. order deleted).
export function uploadOne({ file, orderID, relPath, onProgress, signal, discardURL = false }) {
  return new Promise(async (resolve, reject) => {
    let upload

    const buildUpload = (previousUploads) => {
      upload = new tus.Upload(file, {
        endpoint: TUS_ENDPOINT,
        chunkSize: CHUNK_SIZE,
        retryDelays: RETRY_DELAYS,
        removeFingerprintOnSuccess: true,
        storeFingerprintForResuming: true,
        // Static header only — combining with onBeforeRequest produced
        // "Aldoo X, Aldoo X" because tus appends. uploadMany() awaits auth
        // before construct, so token is guaranteed present here.
        headers: { Authorization: getAuthToken() },
        metadata: {
          orderID,
          relPath: relPath || file.name,
          fileName: file.name,
          filetype: file.type || "application/octet-stream",
        },
        onError: (err) => reject(err),
        onProgress: (bytesSent, bytesTotal) => {
          onProgress?.(bytesSent, bytesTotal || file.size)
        },
        onSuccess: () => resolve({ url: upload.url, size: file.size, relPath }),
      })

      // Resume from prior session if tus-js-client has a stored URL for this
      // file fingerprint. We pick the most recently created one.
      if (previousUploads && previousUploads.length) {
        const best = previousUploads.reduce((a, b) =>
          new Date(a.creationTime) > new Date(b.creationTime) ? a : b)
        upload.resumeFromPreviousUpload(best)
      }

      upload.start()
    }

    // Wire abort: pause without discarding URL by default, so a refresh can
    // continue from the committed offset.
    if (signal) {
      if (signal.aborted) return reject(new Error("aborted"))
      signal.addEventListener("abort", () => {
        try { upload?.abort(discardURL) } catch {}
        reject(new Error("aborted"))
      }, { once: true })
    }

    try {
      const prev = await tus.Upload.findPreviousUploads(file, fingerprintOpts())
      buildUpload(prev)
    } catch (e) {
      // Fingerprint lookup failed — start fresh.
      buildUpload([])
    }
  })
}

// Max parallel tus uploads per batch. The browser caps HTTP/1.1 at 6 per host
// and the API runs as a single pm2 worker, so 4 keeps the user's uplink full
// without starving other in-flight requests (status polls, realtime, etc.).
const UPLOAD_CONCURRENCY = 4

// Upload many files with bounded concurrency. Aggregates progress across the
// batch so the caller sees one number (bytes loaded / total bytes).
// `skipKeys` (Set of "path:size" strings) lists files that the server already
// has committed for this order. They're treated as already done — their bytes
// count toward the running total but no upload is started. Used by resume to
// skip files that completed in a prior session.
export async function uploadMany({ orderID, entries, onProgress, onFileProgress, onFileDone, signal, skipKeys = new Set() }) {
  // Block until credentials are available — first upload after a hard refresh
  // would otherwise race AldooAPI's useEffect and hit 401.
  await awaitAuth()

  const totalBytes = entries.reduce((s, e) => s + e.file.size, 0)

  // Per-file byte counters drive the aggregate progress under concurrency:
  // each tus upload reports its own (sent, total), and the sum across all
  // active uploads is what the caller sees. Without per-file tracking, the
  // last writer would clobber the others on every onProgress.
  const fileSent = new Map() // relPath → bytes uploaded so far
  let aggregate = 0
  const reportAggregate = () => {
    aggregate = 0
    for (const v of fileSent.values()) aggregate += v
    onProgress?.(aggregate, totalBytes)
  }

  // Seed with skip-list bytes so the bar starts at the right offset on resume.
  const queue = []
  for (const { file, path: relPath } of entries) {
    const k = `${relPath}:${file.size}`
    if (skipKeys.has(k)) {
      fileSent.set(relPath, file.size)
      onFileProgress?.(relPath, file.size, file.size)
      onFileDone?.(relPath)
      continue
    }
    fileSent.set(relPath, 0)
    queue.push({ file, relPath })
  }
  reportAggregate()

  let nextIdx = 0
  let firstError = null

  const worker = async () => {
    while (true) {
      if (firstError || signal?.aborted) return
      const i = nextIdx++
      if (i >= queue.length) return
      const { file, relPath } = queue[i]
      try {
        await uploadOne({
          file,
          orderID,
          relPath,
          signal,
          onProgress: (sent /* total */) => {
            fileSent.set(relPath, sent)
            onFileProgress?.(relPath, sent, file.size)
            reportAggregate()
          },
        })
        fileSent.set(relPath, file.size)
        onFileDone?.(relPath)
        reportAggregate()
      } catch (err) {
        // First failure wins — workers see firstError and bail. The caller's
        // catch block surfaces the message to the dialog and cleans up.
        if (!firstError) firstError = err
        return
      }
    }
  }

  const workers = new Array(Math.min(UPLOAD_CONCURRENCY, queue.length)).fill(0).map(worker)
  await Promise.all(workers)
  if (firstError) throw firstError
  if (signal?.aborted) throw new Error("aborted")
}

// Discard ALL stored tus fingerprints for the given file list. Use when the
// order is being deleted / share completed, so localStorage doesn't grow.
export async function discardFingerprints(files) {
  for (const file of files) {
    try {
      const prev = await tus.Upload.findPreviousUploads(file, fingerprintOpts())
      for (const p of prev) {
        try { await tus.Upload.terminate(p.uploadUrl) } catch {}
      }
    } catch {}
  }
}

// Detect whether a given file has a resumable upload in tus storage. Returns
// the most-recent stored upload (with uploadUrl + size) or null.
export async function findResumable(file) {
  try {
    const prev = await tus.Upload.findPreviousUploads(file, fingerprintOpts())
    if (!prev.length) return null
    return prev.reduce((a, b) => new Date(a.creationTime) > new Date(b.creationTime) ? a : b)
  } catch { return null }
}

// Stable per-app fingerprint namespace so multiple tabs/apps on the same
// origin don't collide. tus-js-client passes these to its URL storage key.
function fingerprintOpts() {
  return { endpoint: TUS_ENDPOINT }
}

// Walk all tus-js-client URL storage keys for our endpoint and drop any that
// aren't referenced by an active `transfer:pendingSession`. tus has no
// built-in TTL — without this sweep, every abandoned single-file upload
// leaves a key in localStorage forever and eventually fills the quota.
// `referenced` is a Set of orderIDs that are still live (caller passes the
// current session's orderID, if any).
export function pruneOrphanFingerprints({ keepOrderIDs = new Set(), maxAgeMs = 7 * 24 * 60 * 60 * 1000 } = {}) {
  if (typeof window === "undefined") return
  const prefix = `tus::${TUS_ENDPOINT}::`
  const now = Date.now()
  const toRemove = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || !k.startsWith(prefix)) continue
      let parsed
      try { parsed = JSON.parse(localStorage.getItem(k) || "null") } catch { parsed = null }
      const orderID = parsed?.metadata?.orderID
      const created = parsed?.creationTime ? Date.parse(parsed.creationTime) : 0
      const aged = created > 0 && (now - created) > maxAgeMs
      const orphan = orderID && !keepOrderIDs.has(orderID)
      if (aged || orphan) toRemove.push({ k, url: parsed?.uploadUrl })
    }
  } catch {}
  for (const { k, url } of toRemove) {
    try { localStorage.removeItem(k) } catch {}
    // Best-effort server-side abort. tus.Upload.terminate triggers DELETE on
    // the upload URL; server route maps that to an S3 abortMultipartUpload.
    if (url) tus.Upload.terminate(url).catch(() => {})
  }
}

export const TUS = { TUS_ENDPOINT, CHUNK_SIZE }
